import { useJoyPlaceBySourceIdQuery, HotelProvider, PreferredHotelProviderType } from '@graphql/generated';
import { HotelDetailsIdType, HotelDetailsParamsType, HotelDetailsQueryParams, HotelIdType } from './HotelDetails.types';
import { useQueryParamHelper } from '@shared/core/queryString';
import { addError } from '@shared/utils/logger';
import { useClientReference } from '@shared/hooks/useClientReference';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useCheckInCheckoutDates } from '@shared/hooks/useCheckInCheckoutDates';
import { useTravelWhiteLabelCurrency } from '@shared/hooks/useTravelWhiteLabelCurrency';

const isJoyPlaceId = (id?: string): boolean => {
  if (!id) return false;
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(id);
};

export const useHotelDetailsParams = (joyPlaceId?: string) => {
  const queryParamHelper = useQueryParamHelper();

  // Retrieve query parameters
  const getQueryParam = (param: HotelDetailsQueryParams) => queryParamHelper.getValueString(param);

  const idTypeParam = getQueryParam(HotelDetailsQueryParams.idType) || '';
  const isValidHotelIdType = !!HotelDetailsIdType[idTypeParam as HotelDetailsIdType]; // Validate is valid idtype
  const sourceType = isJoyPlaceId(joyPlaceId) ? HotelDetailsIdType.joy : isValidHotelIdType ? (idTypeParam as HotelDetailsIdType) : HotelDetailsIdType.hotels; // convert value to HotelIdType
  const source = getQueryParam(HotelDetailsQueryParams.source);

  const queryParamEventId = getQueryParam(HotelDetailsQueryParams.eventId);
  const customUrl = getQueryParam(HotelDetailsQueryParams.customUrl);
  const hasExternalLink = getQueryParam(HotelDetailsQueryParams.hasExternalLink);
  const utmParams = {
    utm_source: getQueryParam(HotelDetailsQueryParams.utm_source),
    utm_source_secondary: getQueryParam(HotelDetailsQueryParams.utm_source_secondary),
    utm_medium: getQueryParam(HotelDetailsQueryParams.utm_medium),
    utm_campaign: getQueryParam(HotelDetailsQueryParams.utm_campaign)
  };
  const { clientReference, loadingClientReference } = useClientReference(queryParamEventId);
  const { value: accommodationsGuestHotelProviderType, loading: accommodationsGuestHotelProviderTypeLoading } = useFeatureValue('accommodationsGuestHotelProviderType');

  // Fetch location data via GraphQL
  const { eventInfo, loading: loadingEventInfo } = useEventInfo();
  const eventDate = eventInfo?.finalizedDate?.dateString || undefined;
  const { checkIn, checkOut, isCheckInOrCheckoutValid, message: checkInCheckOutMessage } = useCheckInCheckoutDates({ eventDate });
  const eventId = eventInfo?.eventId || queryParamEventId;

  const { data, loading: loadingData, error: joyPlaceError } = useJoyPlaceBySourceIdQuery({
    variables: {
      payload: { sourceId: joyPlaceId!, sourceType: HotelIdType[sourceType] },
      eventId: eventId,
      preferredProviderType: accommodationsGuestHotelProviderType !== 'joynativecheckout' ? (accommodationsGuestHotelProviderType as PreferredHotelProviderType) : undefined
    },
    batchMode: 'fast',
    skip: !joyPlaceId || !eventId
  });

  // Get joy place details
  const [currency, { loading: loadingCurrency }] = useTravelWhiteLabelCurrency();
  const joyPlace = data?.joyPlaceBySourceId;
  const provider = joyPlace?.redirectMechanism?.[0]?.telemetryDestinationName as HotelProvider;
  const externalPDPUrl = joyPlace?.redirectMechanism?.[0]?.url;
  const hotelWebsite = joyPlace?.url;
  const googleMaps = {
    gplid: joyPlace?.googlePlaceId,
    name: joyPlace?.name,
    address: joyPlace?.address,
    lat: joyPlace?.lat,
    lng: joyPlace?.long
  };

  // Prepare the search params object
  const params: HotelDetailsParamsType = {
    clientReference,
    externalPDPUrl,
    provider,
    hotelWebsite,
    googleMaps,
    customUrl,
    hasExternalLink,
    checkIn,
    checkOut,
    isCheckInOrCheckoutValid,
    sourceType,
    source,
    currencyCode: currency,
    ...utmParams
  };

  const logErrors = () => {
    // eslint-disable-next-line no-console
    console.log(checkInCheckOutMessage);
    // Error handling
    const errors = [
      { condition: checkInCheckOutMessage, message: checkInCheckOutMessage },
      { condition: joyPlaceError, message: 'HotelDetails - GraphQL query failed to fetch HotelJoyPlace data' },
      { condition: !queryParamEventId, message: 'HotelDetails - EventId Missing' },
      { condition: !joyPlaceId, message: 'HotelDetails - JoyPlaceId Missing' },
      { condition: !externalPDPUrl, message: 'HotelDetails - ExternalPDPUrl Missing' },
      { condition: !hotelWebsite, message: 'HotelDetails - HotelWebsite Missing' },
      { condition: !utmParams.utm_source, message: 'HotelSearch - Source Missing' }
    ];

    errors.forEach(({ condition, message }) => condition && addError(message, { context: 'HotelDetails' }));
  };

  return { ...params, loading: loadingData || loadingEventInfo || loadingClientReference || accommodationsGuestHotelProviderTypeLoading || loadingCurrency, logErrors };
};
